import { useQuery } from '@apollo/client';
import { gql } from '@apollo/client';

// GraphQL queries for UI constants
const GET_HAND_PICKED_LANGUAGES = gql`
  query GetHandPickedLanguages {
    handPickedLanguages
  }
`;

const GET_LANGUAGES = gql`
  query GetLanguages($filterQuery: String) {
    languages(filterQuery: $filterQuery)
  }
`;

const GET_HAND_PICKED_TIMEZONES = gql`
  query GetHandPickedTimezones {
    handPickedTimezones {
      value
      offset
    }
  }
`;

const GET_TIMEZONES = gql`
  query GetTimezones($filterQuery: String) {
    timezones(filterQuery: $filterQuery) {
      value
      offset
    }
  }
`;

const GET_REFERRAL_SOURCES = gql`
  query GetReferralSources($filterQuery: String) {
    referralSources(filterQuery: $filterQuery)
  }
`;

const GET_HAND_PICKED_COUNTRIES = gql`
  query GetHandPickedCountries {
    handPickedCountries
  }
`;

const GET_COUNTRIES = gql`
  query GetCountries($filterQuery: String) {
    countries(filterQuery: $filterQuery)
  }
`;

const GET_FILE_UPLOAD_CONSTANTS = gql`
  query GetFileUploadConstants {
    fileUploadConstants {
      profileImage {
        maxSize
      }
      activityImage {
        maxSize
      }
      chatImage {
        maxSize
      }
      brandingImage {
        maxLogoSize
        maxWordmarkSize
        maxBannerSize
      }
      storefrontImage {
        maxProfileImageSize
        maxBannerImageSize
      }
    }
  }
`;

// Hook for languages
export const useLanguages = (filterQuery?: string) => {
  const {
    data: handPickedData,
    loading: handPickedLoading,
    error: handPickedError,
  } = useQuery(GET_HAND_PICKED_LANGUAGES, {
    errorPolicy: 'all',
  });

  const {
    data: allLanguagesData,
    loading: allLanguagesLoading,
    error: allLanguagesError,
  } = useQuery(GET_LANGUAGES, {
    variables: { filterQuery: filterQuery || '' },
    skip: !filterQuery,
    errorPolicy: 'all',
  });

  return {
    handPickedLanguages: handPickedData?.handPickedLanguages || [],
    allLanguages: allLanguagesData?.languages || [],
    loading: handPickedLoading || allLanguagesLoading,
    error: handPickedError || allLanguagesError,
  };
};

// Hook for timezones
export const useTimezones = (filterQuery?: string) => {
  const {
    data: handPickedData,
    loading: handPickedLoading,
    error: handPickedError,
  } = useQuery(GET_HAND_PICKED_TIMEZONES, {
    errorPolicy: 'all',
  });

  const {
    data: allTimezonesData,
    loading: allTimezonesLoading,
    error: allTimezonesError,
  } = useQuery(GET_TIMEZONES, {
    variables: { filterQuery: filterQuery || '' },
    skip: !filterQuery,
    errorPolicy: 'all',
  });

  return {
    handPickedTimezones: handPickedData?.handPickedTimezones || [],
    allTimezones: allTimezonesData?.timezones || [],
    loading: handPickedLoading || allTimezonesLoading,
    error: handPickedError || allTimezonesError,
  };
};

// Hook for referral sources
export const useReferralSources = (filterQuery?: string) => {
  const {
    data: referralSourcesData,
    loading,
    error,
  } = useQuery(GET_REFERRAL_SOURCES, {
    variables: { filterQuery: filterQuery || '' },
    errorPolicy: 'all',
  });

  return {
    referralSources: referralSourcesData?.referralSources || [],
    loading,
    error,
  };
};

// Hook for countries
export const useCountries = (filterQuery?: string) => {
  const {
    data: handPickedData,
    loading: handPickedLoading,
    error: handPickedError,
  } = useQuery(GET_HAND_PICKED_COUNTRIES, {
    errorPolicy: 'all',
  });

  const {
    data: allCountriesData,
    loading: allCountriesLoading,
    error: allCountriesError,
  } = useQuery(GET_COUNTRIES, {
    variables: { filterQuery: filterQuery || '' },
    skip: !filterQuery,
    errorPolicy: 'all',
  });

  return {
    handPickedCountries: handPickedData?.handPickedCountries || [],
    allCountries: allCountriesData?.countries || [],
    loading: handPickedLoading || allCountriesLoading,
    error: handPickedError || allCountriesError,
  };
};

// Shared query for all file upload constants
const useFileUploadConstantsQuery = () => {
  return useQuery(GET_FILE_UPLOAD_CONSTANTS, {
    errorPolicy: 'all',
  });
};

// Hook for profile image upload constants
export const useProfileImageUploadConstants = () => {
  const { data, loading, error } = useFileUploadConstantsQuery();

  return {
    maxProfileImageSize:
      data?.fileUploadConstants?.profileImage?.maxSize ?? 5 * 1024 * 1024, // Default to 5MB
    acceptedProfileImageTypes: ['image/jpeg', 'image/jpg', 'image/png'],
    acceptedProfileImageExtensions: ['.jpg', '.jpeg', '.png'],
    loading,
    error,
  };
};

// Hook for activity image upload constants
export const useActivityImageUploadConstants = () => {
  const { data, loading, error } = useFileUploadConstantsQuery();

  return {
    maxActivityImageSize:
      data?.fileUploadConstants?.activityImage?.maxSize ?? 50 * 1024 * 1024, // Default to 50MB
    acceptedActivityImageTypes: ['image/jpeg', 'image/jpg', 'image/png'],
    acceptedActivityImageExtensions: ['.jpg', '.jpeg', '.png'],
    loading,
    error,
  };
};

// Hook for chat/comment image upload constants
export const useChatImageUploadConstants = () => {
  const { data, loading, error } = useFileUploadConstantsQuery();

  return {
    maxChatImageSize:
      data?.fileUploadConstants?.chatImage?.maxSize ?? 5 * 1024 * 1024, // Default to 5MB
    acceptedChatImageTypes: ['image/jpeg', 'image/jpg', 'image/png'],
    acceptedChatImageExtensions: ['.jpg', '.jpeg', '.png'],
    loading,
    error,
  };
};

// Hook for branding/appearance image upload constants
export const useBrandingImageUploadConstants = () => {
  const { data, loading, error } = useFileUploadConstantsQuery();

  return {
    maxLogoSize:
      data?.fileUploadConstants?.brandingImage?.maxLogoSize ?? 10 * 1024 * 1024, // Default to 10MB
    maxWordmarkSize:
      data?.fileUploadConstants?.brandingImage?.maxWordmarkSize ??
      10 * 1024 * 1024, // Default to 10MB
    maxBannerSize:
      data?.fileUploadConstants?.brandingImage?.maxBannerSize ??
      50 * 1024 * 1024, // Default to 50MB
    acceptedBrandingImageTypes: ['image/jpeg', 'image/jpg', 'image/png'],
    acceptedBrandingImageExtensions: ['.jpg', '.jpeg', '.png'],
    loading,
    error,
  };
};

// Hook for storefront image upload constants
export const useStorefrontImageUploadConstants = () => {
  const { data, loading, error } = useFileUploadConstantsQuery();

  return {
    maxStorefrontProfileImageSize:
      data?.fileUploadConstants?.storefrontImage?.maxProfileImageSize ??
      5 * 1024 * 1024, // Default to 5MB
    maxStorefrontBannerImageSize:
      data?.fileUploadConstants?.storefrontImage?.maxBannerImageSize ??
      50 * 1024 * 1024, // Default to 50MB
    acceptedStorefrontImageTypes: ['image/jpeg', 'image/jpg', 'image/png'],
    acceptedStorefrontImageExtensions: ['.jpg', '.jpeg', '.png'],
    loading,
    error,
  };
};
